import { supabase } from "@/lib/supabase";
import {
  call,
  debounce,
  fork,
  put,
  select,
  take,
  takeEvery,
} from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import { EventChannel, eventChannel } from "redux-saga";

import {
  fetchBrand7dSummary,
  fetchBrandSeries,
  fetchDailyAverageModel,
  fetchDailyAverageModelFailure,
  fetchDailyAverageModelSuccess,
  fetchLastUpdatesBrand,
  fetchLastUpdatesBrandsFailure,
  fetchLastUpdatesBrandsSuccess,
  fetchScoreModel,
  fetchScoreModelFailure,
  fetchScoreModelSuccess,
  fetchScorePrompt,
  fetchScorePromptFailure,
  fetchScorePromptSuccess,
  fetchOverviewScoresFromDaily,
  fetchOverviewTopModel,
  setLoadingDailyAverageModel,
  updateScoresWS,
  setDailyAverageModelRangeValues,
  fetchLastUpdatesLoading,
  fetchBrandByPromptsSeries,
} from "./slice";
import { Scores } from "./types";
import { ScoreDailyAverageResponse, ScoreResponse} from "./response.types";


import { fetchBrand7dSummarySaga } from "./sagas/fetchBrand7d";
import { applySagaError } from "../utils/saga";
import { throwOnSupabaseError } from "../../utils/errors";
import {
  SCORES_TABLE_NAME,
} from "../const";
import { selectAuthAccountId } from "../selectors/auth";

import { LastUpdates } from "../../types/scores";
import { format } from "date-fns";
import { transformSupabaseDates } from "../../utils/transformers";
import {
  selectBrandLoading,
  selectSelectedBrandState,
  selectViewBrandIdState,
} from "../selectors/brands";
import { selectModelLoading } from "../selectors/models";
import {
  selectOverviewDateFilterState,
  selectSelectedModelIdState,
} from "../selectors/overview";
import {
  DateFilter,
  DateSelectionMode,
  OverviewPeriod,
} from "../../components/DatePeriodSelector/types";
import { Brand } from "../../types/brand";
import { fetchBrandSeriesSaga } from "./sagas/fetchBrandSeries";

import { UsersMetaRealtimePayload } from "./payload.types";
import { FetchDailyAverageModelPayload } from "./payload.types";
import { FetchScoresPromptPayload } from "./payload.types";
import { FetchScoresModelPayload } from "./payload.types";
import { fetchBrandByPromptsSeriesSaga } from "./sagas/fetchBrandByPromptsSeries";
import { scoresApi } from "../../api/scores.api";
import { reduceModelScoreDailyRowsToSeries } from "./utils/modelScoreDailyFromRows";
import { fetchOverviewScoresSaga } from "./sagas/fetchOverviewScores";
import { fetchOverviewTopModelSaga } from "./sagas/fetchOverviewTopModel";





const DEFAULT_MODEL_SERIES_DAYS = 16;

export function* fetchDailyAverageModelSaga(
  action: PayloadAction<FetchDailyAverageModelPayload>,
) {
  try {
    yield put(setLoadingDailyAverageModel());
    const userID: string | null = yield select(selectAuthAccountId);
    if (userID == null) {
      yield put(fetchDailyAverageModelFailure());
      return;
    }

    const days = action.payload.days ?? DEFAULT_MODEL_SERIES_DAYS;
    const rows: ScoreDailyAverageResponse[] = yield call(scoresApi.fetchBrandScoreByModelDays, {
      userID,
      brandId: action.payload.brand_id,
      days,
    });

    const { scoresByModel, rangeValues } = reduceModelScoreDailyRowsToSeries(rows);

    yield put(setDailyAverageModelRangeValues(rangeValues));
    yield put(fetchDailyAverageModelSuccess(scoresByModel));
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "scores.fetchDailyAverageModel",
      failureAction: fetchDailyAverageModelFailure,
    });
  }
}

export function* fetchScorePromptSaga(
  action: PayloadAction<FetchScoresPromptPayload>,
) {
  try {
    const userID: string | null = yield select(selectAuthAccountId);
    const { data, error } = yield call(() =>
      supabase
        .from(SCORES_TABLE_NAME)
        .select("*")
        .eq("user_id", userID)
        .eq("prompt_id", action.payload.prompt_id)
        .in("brand_id", action.payload.brand_ids)
        .gte("created_at", `${action.payload.date}T00:00:00`)
        .lte("created_at", `${action.payload.date}T23:59:59`)
        .order("created_at", { ascending: true })
    );

    throwOnSupabaseError(error);

    const scoresByPrompt = data.reduce((acc: Scores, score: ScoreResponse) => {
      const brandId = score.brand_id;
      const promptId = score.prompt_id;
      const modelId = score.model_id;

      if (!acc[promptId]) acc[promptId] = {};
      if (!acc[promptId][brandId]) acc[promptId][brandId] = {};

      acc[promptId][brandId][modelId] = score;

      return acc;
    }, {});

    yield put(
      fetchScorePromptSuccess({
        prompt_id: action.payload.prompt_id,
        scores: Object.keys(scoresByPrompt).length ? scoresByPrompt : null,
      }),
    );
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "scores.fetchScorePrompt",
      failureAction: fetchScorePromptFailure,
    });
  }
}

export function* fetchScoreModelSaga(action: PayloadAction<FetchScoresModelPayload>) {
  try {
    const userID: string | null = yield select(selectAuthAccountId);
    const { data, error } = yield call(() =>
      supabase
        .from(SCORES_TABLE_NAME)
        .select("*")
        .eq("user_id", userID)
        .eq("model_id", action.payload.model_id)
        .in("brand_id", action.payload.brand_ids)
        .gte("created_at", `${action.payload.date}T00:00:00`)
        .lte("created_at", `${action.payload.date}T23:59:59`)
        .order("created_at", { ascending: true })
    );

    throwOnSupabaseError(error);

    const scoresByModel = data.reduce((acc: Scores, score: ScoreResponse) => {
      const brandId = score.brand_id;
      const promptId = score.prompt_id;
      const modelId = score.model_id;

      if (!acc[modelId]) acc[modelId] = {};
      if (!acc[modelId][brandId]) acc[modelId][brandId] = {};

      acc[modelId][brandId][promptId] = score;

      return acc;
    }, {});

    yield put(
      fetchScoreModelSuccess({
        model_id: action.payload.model_id,
        scores: Object.keys(scoresByModel).length ? scoresByModel : null,
      }),
    );
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "scores.fetchScoreModel",
      failureAction: fetchScoreModelFailure,
    });
  }
}

export function* fetchLastUpdatesBrandSaga(action: PayloadAction<number>) {
  try {
    yield put(fetchLastUpdatesLoading());
    const brandId = action.payload;
    const userID: string | null = yield select(selectAuthAccountId);

    const { data, error } = yield call(() =>
      supabase
        .from(SCORES_TABLE_NAME)
        .select("*")
        .eq("user_id", userID)
        .eq("brand_id", brandId)
        .order("created_at", { ascending: false })
        .limit(1)
    );

    throwOnSupabaseError(error);

    const lastUpdatesByBrand = data.reduce(
      (acc: LastUpdates, score: ScoreResponse) => {
        const brandId = String(score.brand_id);
        if (!acc[brandId] || new Date(score.created_at) > new Date(acc[brandId])) {
          acc[brandId] = score.created_at;
        }
        return acc;
      },
      {},
    );

    yield put(
      fetchLastUpdatesBrandsSuccess(transformSupabaseDates(lastUpdatesByBrand)),
    );
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "scores.fetchLastUpdatesBrand",
      failureAction: fetchLastUpdatesBrandsFailure,
    });
  }
}

function createScoresRealtimeChannel() {
  return eventChannel<UsersMetaRealtimePayload>((emitter) => {
    const rtChannel = supabase
      .channel("scores-users-meta")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "users_meta",
        },
        (payload) => {
          const typed = payload as unknown as UsersMetaRealtimePayload;
          if (typed.old?.last_scores_update !== typed.new?.last_scores_update) {
            emitter(typed);
          }
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(rtChannel);
    };
  });
}

function* updateScoresWSSaga() {
  try {
    const selectedBrand: Brand | null = yield select(selectSelectedBrandState);
    const viewBrandId: number | null = yield select(selectViewBrandIdState);
    const brandsLoading: boolean = yield select(selectBrandLoading);
    const dateFilter: DateFilter = yield select(selectOverviewDateFilterState);
    const selectedModelId: number | null = yield select(selectSelectedModelIdState);
    const modelsLoading: boolean = yield select(selectModelLoading);

    yield put(fetchBrand7dSummary());
    yield put(fetchBrandSeries({ days: 16 }));

    if (selectedBrand) {
      yield put(fetchBrandByPromptsSeries({ brand_id: selectedBrand.id, days: 16 }));
      yield put(fetchDailyAverageModel({ brand_id: selectedBrand.id, days: 16 }));
    }

    if (viewBrandId != null) {
      yield put(fetchLastUpdatesBrand(viewBrandId));
    }

    const hasBrandContext = selectedBrand != null || viewBrandId != null;
    if (!brandsLoading && hasBrandContext) {
      const modelIdPayload =
        selectedModelId != null ? { model_id: selectedModelId } : {};
      if (dateFilter.type === DateSelectionMode.Date) {
        const datePayload = { date: format(dateFilter.dateTs, "yyyy-MM-dd") };
        yield put(fetchOverviewScoresFromDaily({ ...datePayload, ...modelIdPayload }));
        if (!modelsLoading) {
          yield put(fetchOverviewTopModel(datePayload));
        }
      } else {
        const period: 7 | 30 =
          dateFilter.period === OverviewPeriod.ThirtyDays ? 30 : 7;
        const periodPayload = { period };
        yield put(fetchOverviewScoresFromDaily({ ...periodPayload, ...modelIdPayload }));
        if (!modelsLoading) {
          yield put(fetchOverviewTopModel(periodPayload));
        }
      }
    }
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "scores.updateScoresWS",
      notifyUser: false,
    });
  }
}

function* watchScoresInserts() {
  const channel: EventChannel<UsersMetaRealtimePayload> = yield call(createScoresRealtimeChannel);

  try {
    while (true) {
      yield take(channel);
      yield put(updateScoresWS());
    }
    
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "scores.watchUsersMetaRealtime",
      notifyUser: false,
    });
  } finally {
    channel.close();
  }
}

export function* scoresSaga() {
  yield takeEvery(fetchBrand7dSummary.type, fetchBrand7dSummarySaga);
  yield takeEvery(fetchBrandSeries.type, fetchBrandSeriesSaga);
  yield takeEvery(fetchBrandByPromptsSeries.type, fetchBrandByPromptsSeriesSaga);
  yield takeEvery(fetchDailyAverageModel.type, fetchDailyAverageModelSaga);
  yield takeEvery(fetchScorePrompt.type, fetchScorePromptSaga);
  yield takeEvery(fetchScoreModel.type, fetchScoreModelSaga);
  yield takeEvery(fetchLastUpdatesBrand.type, fetchLastUpdatesBrandSaga);
  yield takeEvery(fetchOverviewScoresFromDaily.type, fetchOverviewScoresSaga);
  yield takeEvery(fetchOverviewTopModel.type, fetchOverviewTopModelSaga);
  yield debounce(5000, updateScoresWS.type, updateScoresWSSaga);
  yield fork(watchScoresInserts);
}